<template>
    <div class="appointment-confirmation container mx-auto" v-if="appointmentData">
        <div class="confirm-container mb-8">
            <div class="confirm-card">
                <card v-if="appointmentData.clinic.ClinicID != videoClinicID" class="mb-8 card-link">
                    <a href="https://www.ascenti.co.uk/articles/about-physio" target="_blank" rel="noopener">
                        <span class="icon"><img src="../assets/images/clipboard.svg" alt="" /></span>

                        <span class="content">
                            <span class="block">What to expect</span>
                            <span class="block text-sm">Find out more about what happens during your appointment, what to wear and how physio works</span>
                        </span>

                        <span class="caret"><img src="../assets/images/caret-right.svg" alt="" /></span>
                    </a>
                </card>

                <div class="appointment-confirmation__body-text" v-if="appointmentData.clinic.ClinicID != videoClinicID">
                    <div class="mt-4 mb-4">Coronavirus</div>

                    <p>Thank you for booking a face-to-face consultation with us.</p>

                    <p>Please do not attend your appointment if you have Covid-19 related symptoms, have a high temperature or have tested positive for Covid-19.</p>

                    <p>If you need to cancel or rearrange your appointment, please let us know at least 24 hours before it is due to take place.</p>
                </div>

                <card class="text-center" :dark="true">
                    <div class="py-2">Physiotherapy services at the touch of a button</div>
                    <hr>
                    <div v-if="appointmentData.clinic.ClinicID == videoClinicID" class="pt-2 pb-4 text-sm">
                        <img class="inline w-1/3 pt-2 pb-4" src="../assets/images/communications.svg" alt="" />
                        <div class="w-2/3 mx-auto">You will need to download the Ascenti Physio app which is where your video appointment will take place. We will email you with instructions and login details soon.</div>
                    </div>
                    <div v-else class="pt-2 pb-4 text-sm">
                        <div>You will shortly receive an email inviting you to join</div>
                        <img class="inline w-1/3" src="../assets/images/ascenti-physio-logo-white.png" alt="" />
                        <div class="w-2/3 mx-auto">Ascenti's exercise and rehabilitation app that will support you on your journey to recovery.</div>
                    </div>
                </card>

                <div class="mt-4">Why join the Ascenti Physio app:</div>

                <ul class="check-list">
                    <li>Be supported every step of the way with 24/7 access to expert advice and tailored exercise programs.</li>
                    <li>Carry out exercises in the comfort of your home with guided videos that can be downloaded and viewed at any time.</li>
                    <li>Set yourself in-app reminders so that you always remember to complete your exercises.</li>
                    <li>Record your progress and leave real-time feedback, allowing your physiotherapist to track your recovery and tailor your program as needed.</li>
                </ul>

                <hr>

                <div class="mt-6 mb-6 text-sm">Log in before your first appointment to kick-start your treatment with us. </div>
            </div>

            <div class="confirm-card">
                <card v-if="appointmentData.clinic.ClinicID == videoClinicID" class="mb-8 card-link">
                    <a :href="whatToExpectUrl" target="_blank" rel="noopener">
                        <span class="icon"><img src="../assets/images/clipboard.svg" alt="" /></span>

                        <span class="content">
                            <span class="block">What to expect</span>
                            <span class="block text-sm">Find out more about what happens during your appointment, what to wear and how physio works</span>
                        </span>

                        <span class="caret"><img src="../assets/images/caret-right.svg" alt="" /></span>
                    </a>
                </card>

                <card class="card-link" :dark="true">
                    <a href="tel:+443306780850">
                        <span class="icon"><img src="../assets/images/phone-white.svg" alt="" /></span>

                        <span class="content no-caret">
                            <span class="block">Cancel or change</span>
                            <span class="block text-sm text-thin">If you need to cancel or change your appointment, please call us as soon you can on <strong>0330 678 0850</strong></span>
                        </span>
                    </a>
                </card>

                <card v-if="appointmentData.clinic.ClinicID != videoClinicID" class="mt-8 p-8 flex flex-col sm:flex-row md:flex-col lg:flex-row justify-between">
                    <div class="w-full sm:w-1/2 md:w-full lg:w-1/2 p-2 map">
                        <img :src="mapImageUrl" alt="" />
                    </div>

                    <div class="w-full sm:w-1/2 md:w-full lg:w-1/2 p-2">
                        <div>
                            {{ appointmentData.clinic.Address1 }},
                            {{ appointmentData.clinic.Postcode }}
                        </div>

                        <a class="btn btn-secondary mt-2" :href="mapDirectionsUrl" rel="noopener nofollow noreferrer" target="_blank">
                            Get directions
                        </a>

                        <a v-if="clinicUrl" class="btn btn-secondary mt-2" :href="clinicUrl">More info</a>
                    </div>
                </card>

                <card class="card-link no-icon mt-4" :dark="appointmentData.clinic.ClinicID != videoClinicID">
                    <a :href="faqsUrl" target="_blank" rel="noopener">
                        <span class="content">
                            <span class="block">Frequently Asked Questions</span>
                        </span>

                        <img v-if="appointmentData.clinic.ClinicID == videoClinicID" class="caret" src="../assets/images/caret-right.svg" alt="" />
                        <img v-else class="caret" src="../assets/images/caret-right-white.svg" alt="" />
                    </a>
                </card>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapActions, mapState } from 'vuex';

    import Card from '../components/Block/Card';
    import { Prices } from '../lib/Prices';

    export default {
        name: 'appointment-confirmation',

        components: {
            Card,
        },

        computed: {
            mapDirectionsUrl() {
                return 'https://www.google.co.uk/maps/dir//' // Keep double slash here so that clinic is the destination.
                    + encodeURIComponent(this.appointmentData.clinic.ClinicName) + ','
                    + encodeURIComponent(this.appointmentData.clinic.Address1) + ','
                    + encodeURIComponent(this.appointmentData.clinic.Postcode);
            },

            mapImageUrl() {
                return 'https://maps.googleapis.com/maps/api/staticmap?center='
                    + encodeURIComponent(this.appointmentData.clinic.ClinicName) + ','
                    + encodeURIComponent(this.appointmentData.clinic.Address1) + ','
                    + encodeURIComponent(this.appointmentData.clinic.Postcode)
                    + '&zoom=9&size=530x250&key=' + process.env.VUE_APP_GOOGLE_API_KEY;
            },

            videoClinicID() {
                return process.env.VUE_APP_VIDEO_CLINIC_ID;
            },

            whatToExpectUrl() {
                const isVideoAppointment = this.appointmentData.clinic.ClinicID == this.videoClinicID;
                return isVideoAppointment ? 'https://www.ascenti.co.uk/article/online-physio-treatment' : 'https://www.ascenti.co.uk/articles/about-physio';
            },

            faqsUrl() {
                const isVideoAppointment = this.appointmentData.clinic.ClinicID == this.videoClinicID;
                return isVideoAppointment ? 'https://www.ascenti.co.uk/article/virtual-physio-faqs' : 'https://www.ascenti.co.uk/article/help-centre-your-questions-answered';
            },

            ...mapState('voucher', [
                'voucherCode',
            ]),
        },

        data() {
            let appointmentData;

            try {
                appointmentData = JSON.parse(window.localStorage.getItem('appointment'));
            } catch (error) {
                appointmentData = null;
            }

            return {
                appointmentData,
                clinicUrl: null,
            };
        },

        mounted() {
            if (!this.appointmentData) {
                this.$router.push({ name: 'home' });
            }

            this.$scrollTo('#app');

            this.sendEcommerceTracking();

            this.clearVuexStore();

            this.hideNavigation();

            this.setTheme('dark');

            this.showComponent({ name: 'appointment-overview' });

            this.setFooterMessage(null);

            this.loadClinicUrl();
        },

        methods: {
            ...mapActions('header', [
                'showComponent',
                'hideNavigation',
                'setTheme',
            ]),

            ...mapActions('footer', [
                'setFooterMessage',
            ]),

            ...mapActions('appointment', [
                'setAppointmentData',
                'setDate',
                'setPlace',
                'setPlaceId',
                'setSort',
            ]),

            ...mapActions('appointments', [
                'clearReservedAppointment',
            ]),

            ...mapActions('checkout', [
                'setPatientDetails',
                'setInjuryDetails',
                'setPaymentDetails',
            ]),

            ...mapActions('voucher', [
                'clearVoucherCode',
            ]),

            sendEcommerceTracking() {
                const isVirtual = this.appointmentData.clinic.ClinicID == this.videoClinicID;
                const priceCharged = this.total() - this.discount();

                const params = {
                    ecommerce: {
                        purchase: {
                            actionField: {
                                id: this.appointmentData.appointment.AppointmentID,
                                revenue: priceCharged.toString() + '.00',
                                coupon: this.voucherCode ? window._.first(this.voucherCode.codes) : ''
                            },
                            products: [{
                                name: isVirtual ? 'Virtual' : 'Clinic',
                                id: isVirtual ? 2 : 1,
                                price: priceCharged.toString() + '.00'
                            }]
                        }
                    }
                }

                window.dataLayer.push(params);
            },

            total() {
                if (this.appointmentData.clinic.ClinicID == process.env.VUE_APP_VIDEO_CLINIC_ID) {
                    return Prices.video();
                }

                if (this.appointmentData.clinic.ClinicTier === 'CentralLondon') {
                    return Prices.centralLondon();
                }

                if (Prices.hasExceptionPrice() && window._.includes(
                  Prices.exceptionClinicIds(),
                  parseInt(this.appointmentData.clinic.ClinicID))
                ) {
                    return Prices.exception();
                }

                return this.appointmentData.clinic.InsideM25 ? Prices.insideM25() : Prices.outsideM25();
            },

            discount() {
                if (!this.voucherCode) {
                    return 0;
                }

                if (this.appointmentData.clinic.ClinicTier === 'CentralLondon') {
                    return this.voucherCode.discount_central_london;
                }

                if (this.appointmentData.clinic.ClinicTier === 'Virtual') {
                    return this.voucherCode.discount_video;
                }

                return this.appointmentData.clinic.InsideM25 ? this.voucherCode.discount_inside_m25 : this.voucherCode.discount_outside_m25;
            },

            loadClinicUrl() {
                const url = process.env.VUE_APP_CLINIC_DETAILS_URL + this.appointmentData.clinic.ClinicID;

                window.axios.get(url).then(response => {
                    if (response.data.clinic) {
                        this.clinicUrl = response.data.clinic.url;
                    }
                });
            },

            clearVuexStore() {
                this.setAppointmentData(null);
                this.setDate(null);
                this.setPlace(null);
                this.setPlaceId(null);
                this.setSort(null);
                this.clearReservedAppointment();
                this.clearVoucherCode();

                this.setPatientDetails({
                    title: '',
                    firstName: '',
                    lastName: '',
                    phone: '',
                    email: '',
                    marketing: false,
                    address1: '',
                    address2: '',
                    city: '',
                    postcode: '',
                });

                this.setInjuryDetails({
                    newOrExisting: 'new',
                    bodyPart: '',
                    additionalInfo: '',
                    dateOfBirth: '',
                    gender: '',
                    specificAccessNeeds: false,
                    acceptTerms: false,
                });

                this.setPaymentDetails({
                    altBillingAddress: false,
                    address1: '',
                    address2: '',
                    city: '',
                    postcode: '',
                });
            },
        }
    }
</script>

<style lang="scss" scoped>
    .confirm-container {
        @apply w-full;

        @screen md {
            @apply flex flex-row justify-center items-stretch;
        }
    }

    .confirm-card {
        @apply relative mx-4 mt-8 mb-0;

        @screen md {
            @apply w-1/2;
        }

        @screen lg {
            @apply w-2/5;
        }

        hr {
            @apply m-0;
        }
    }

    .card-link {
        &.hover {
            @apply bg-gray;
        }

        &.card-dark.hover {
            @apply bg-blue-dark;
        }

        > a {
            @apply flex flex-row w-full py-5 items-center;

            > span {
                @apply block;

                img {
                    @apply inline;
                }
            }

            span.icon {
                @apply w-2/12 mx-4 text-center;
            }

            span.content {
                @apply w-8/12 text-left;

                &.no-caret {
                    @apply w-10/12 text-left pr-8 -ml-4;
                }
            }

            span.caret {
                @apply w-2/12 text-center;
            }
        }

        &.no-icon {
            > a {
                @apply justify-between;

                span.content {
                    @apply w-10/12 pl-8;
                }

                img.caret {
                    @apply block mr-8;
                }
            }
        }
    }

    .check-list {
        @apply mt-4 pb-3;

        li {
            @apply mb-4 pl-10 text-sm;
            background: url(../assets/images/tick.svg) no-repeat center left;
        }
    }

    .card {
        .btn {
            @apply px-4 text-center;
        }
    }

    .map {
        @apply overflow-hidden;

        img {
            @apply rounded;
            object-fit: cover;
            width: 100%;
            height: auto;

            @screen md {
                height: 100%;
                max-height: 150px;
                width: auto;
            }
        }
    }

    .appointment-confirmation__body-text {
        > .mb-4 {
            font-size: 1.875rem;
        }

        ul, p {
            margin-bottom: 20px;
        }

        ul {
            list-style: initial;
            padding-left: 17px;
        }
    }
</style>
